// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { createRating, getRating, telechargerRapport, telechargerExcel, estEnCours } from '../services/api';
import { useI18n } from '../i18n';

// La carte n'affiche NI score NI nombre d'échantillons : seuls les compteurs
// d'avis y figurent, avec leurs propres couleurs. Vivaient pourtant ici, sans
// jamais être rendues, une table `SCORE_COLORS` et quatre variables —
// `conformityScore`, `scoreColor`, `totalSamples`, `positiveSamples`. La table
// était de surcroît indexée sur l'enum disparu EXCELLENT / BON / ACCEPTABLE /
// INSUFFISANT. Supprimées le 15/08/2026 : ce code mort décrivait un affichage
// inexistant et m'a fait annoncer à tort que la couleur du score changerait.
// Rétablir un badge de score est une décision produit, pas une remise en service.

// `lectureSeule` (15/09/2026) : l'analyse d'un collègue, vue par l'administrateur
// de la licence. Téléchargements permis ; signaler un avis, annoter, noter et
// supprimer masqués — ce sont des actes de l'auteur, et le serveur les lui
// réserve de toute façon (cf `core.droits.peut_consulter_analyse`).
//
// `onCancel` (01/10/2026, demande de Marc) : une analyse EN COURS continue sur
// le serveur même onglet fermé ; sa carte offre alors « Voir » (la page de
// l'analyse, avec sa progression) et « Annuler ». La confirmation est à la
// charge de l'appelant, qui recharge ensuite la liste.
const AnalysisCard = ({ analysis, onDelete, onAnnotate, onSignaler, onCancel, isAdmin, lectureSeule = false }) => {
  const { t, lang } = useI18n();
  const [, setLocation] = useLocation();
  const STATUS_CONFIG = {
    PENDING:    { variant: 'default', label: t('status_pending'),    icon: '⏳' },
    PROCESSING: { variant: 'default', label: t('status_processing'), icon: '⚙️' },
    COMPLETED:  { variant: 'success', label: t('status_completed'),  icon: '✅' },
    ERROR:      { variant: 'error',   label: t('status_error'),      icon: '❌' },
    // ⚠️ Sans cette ligne, une annulée retombait sur PENDING : « En attente »
    // (constaté en prod le 01/10/2026 — visible des comptes Evalens, dont la
    // liste n'écarte pas les annulées).
    CANCELLED:  { variant: 'default', label: t('status_cancelled'),  icon: '🚫' },
  };
  const config     = STATUS_CONFIG[analysis.status] || STATUS_CONFIG.PENDING;
  const analysisId = analysis.id || analysis.analysis_id;

  // Parse metadata
  let meta = {};
  try {
    const m = analysis.metadata;
    meta = m ? (typeof m === 'string' ? JSON.parse(m) : m) : {};
  } catch (_) {}

  const nbConformes      = meta.nb_conformes     != null ? meta.nb_conformes     : null;
  const nbReserves       = meta.nb_reserves      != null ? meta.nb_reserves      : null;
  const nbNonConformes   = meta.nb_non_conformes != null ? meta.nb_non_conformes : null;
  const hasConformityCounts = nbConformes !== null || nbReserves !== null || nbNonConformes !== null;

  // Rating state
  const [showRating, setShowRating]   = useState(false);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [existingRating, setExistingRating] = useState(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const handleOpenRating = async () => {
    setShowRating(true);
    try {
      const data = await getRating(analysisId);
      if (data.rating) {
        setExistingRating(data.rating);
        setRatingScore(data.rating.score);
        setRatingComment(data.rating.comment || '');
      }
    } catch (_) {}
  };

  const handleSubmitRating = async () => {
    if (!ratingScore) return;
    setRatingLoading(true);
    try {
      await createRating(analysisId, { score: ratingScore, comment: ratingComment });
      setRatingSubmitted(true);
      setExistingRating({ score: ratingScore, comment: ratingComment });
      setTimeout(() => setShowRating(false), 1200);
    } catch (e) {
      alert(e.message);
    } finally {
      setRatingLoading(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  // `fetch` + en-tête, jamais `?token=` dans l'URL (cf `telechargerFichier`).
  const handleDownload = (type) => {
    const telecharger = type === 'html' ? telechargerRapport : telechargerExcel;
    telecharger(analysisId).catch(e => alert(e.message));
  };

  // Déterminer quels boutons de téléchargement afficher
  const hasHtml  = !!analysis.report_html_path;
  const hasExcel = !!analysis.report_excel_path;

  return (
    <Card className="hover:shadow-xl transition-all duration-200" style={analysis.project_name ? { border: '2px solid #059669' } : {}}>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            {analysis.project_name && (
              <p className="text-xs mb-1" style={{ color: '#059669', fontWeight: 600 }}>
                {t('project_badge_label')} : {analysis.project_name}
              </p>
            )}
            <h3
              className={`font-bold mb-1 truncate ${analysis.project_name ? 'text-base' : 'text-lg'}`}
              style={{ fontFamily: 'Futura, Montserrat, sans-serif', color: 'var(--black)' }}
              title={analysis.filename}
            >
              {analysis.filename}
            </h3>
            <p className="text-xs" style={{ color: 'rgba(12,12,12,0.6)' }}>
              {formatDate(analysis.upload_date || analysis.created_at)}
            </p>
            {isAdmin && !lectureSeule && analysis.user_id && (
              <p className="text-xs mt-1" style={{ color: 'var(--blue)' }}>
                👤 {analysis.user_email
                  ? analysis.user_email.split('@')[0]
                  : analysis.user_id.substring(0, 8) + '…'}
              </p>
            )}
            {lectureSeule && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                {t('analyse_lecture_seule').replace('{auteur}', analysis.user_email || '—')}
              </p>
            )}
          </div>
          <Badge variant={config.variant} className="ml-2 flex-shrink-0">
            {config.icon} {config.label}
          </Badge>
        </div>

        {/* Compteurs conformité */}
        {analysis.status === 'COMPLETED' && hasConformityCounts && (
          <div
            className="mb-3 p-3 rounded-lg"
            style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', fontSize: 12 }}>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>{nbConformes ?? 0} {t('favorable_label')}</span>
              <span style={{ color: '#d1d5db' }}>·</span>
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>{nbReserves ?? 0} {t('avec_reserve_label')}</span>
              <span style={{ color: '#d1d5db' }}>·</span>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>{nbNonConformes ?? 0} {t('defavorable_label')}</span>
            </div>

            {/* Boucle de retour « cet avis est faux » — S3, 25/08/2026.
                Placé ICI, sous les compteurs, et nulle part ailleurs : c'est le
                seul endroit du produit où les trois positions sont déjà sous les
                yeux de la personne. Un lien discret plutôt qu'un bouton plein —
                l'action normale reste de télécharger son rapport ; contester est
                l'exception, et doit le rester visuellement. */}
            {onSignaler && !lectureSeule && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button
                  onClick={() => onSignaler(analysis)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontSize: 11, color: 'var(--text-muted)', textDecoration: 'underline',
                  }}
                >
                  {t('retour_btn')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Boutons téléchargement */}
        {analysis.status === 'COMPLETED' && (hasHtml || hasExcel) && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {hasHtml && (
              <Button variant="default" className="flex-1" size="sm" onClick={() => handleDownload('html')}>
                📄 PDF
              </Button>
            )}
            {hasExcel && (
              <Button variant="outline" className="flex-1" size="sm" onClick={() => handleDownload('excel')}>
                📊 Excel
              </Button>
            )}
          </div>
        )}

        {/* Boutons Annoter + Évaluer */}
        {analysis.status === 'COMPLETED' && !lectureSeule && (
          <div className="flex gap-2 mt-2">
            {onAnnotate && (
              <Button variant="outline" className="flex-1" size="sm" onClick={() => onAnnotate(analysis)}>
                {t('btn_annotate')}
              </Button>
            )}
            <Button variant="outline" className="flex-1" size="sm" onClick={handleOpenRating}>
              {existingRating ? `⭐ ${existingRating.score}/5` : t('btn_rate')}
            </Button>
          </div>
        )}

        {/* Formulaire notation inline */}
        {showRating && (
          <div
            style={{
              marginTop: 10, padding: '12px 14px',
              background: '#fafafa', borderRadius: 8,
              border: '1px solid #e5e7eb',
            }}
          >
            {ratingSubmitted ? (
              <p style={{ color: '#22c55e', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                {t('rating_thanks')}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 8 }}>
                  {t('rating_title')}
                </p>
                {/* Étoiles */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRatingScore(star)}
                      onMouseEnter={() => setRatingHover(star)}
                      onMouseLeave={() => setRatingHover(0)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 24,
                        color: star <= (ratingHover || ratingScore) ? '#f59e0b' : '#d1d5db',
                        transition: 'color 0.1s',
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder={t('rating_comment_ph')}
                  rows={2}
                  style={{
                    width: '100%', padding: '6px 8px',
                    borderRadius: 6, border: '1px solid #d1d5db',
                    fontSize: 12, resize: 'vertical', boxSizing: 'border-box',
                    marginBottom: 8,
                  }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="sm" onClick={handleSubmitRating} disabled={!ratingScore || ratingLoading}>
                    {ratingLoading ? '⏳' : t('rating_submit')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowRating(false)}>
                    {t('pref_cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Analyse EN COURS (traitement ou file) : la voir, l'annuler */}
        {estEnCours(analysis) && (
          <div className="flex gap-2 mt-3">
            <Button variant="outline" className="flex-1" size="sm"
                    onClick={() => setLocation(`/analysis/${analysisId}`)}>
              {t('analyse_btn_voir')}
            </Button>
            {onCancel && !lectureSeule && (
              <Button variant="destructive" className="flex-1" size="sm" onClick={() => onCancel(analysisId)}>
                {t('analyse_btn_annuler')}
              </Button>
            )}
          </div>
        )}

        {/* Supprimer : dépôt jamais lancé (PENDING hors file) ou erreur */}
        {((analysis.status === 'PENDING' && !estEnCours(analysis)) || analysis.status === 'ERROR')
          && onDelete && !lectureSeule && (
          <div className="mt-3">
            <Button variant="destructive" className="w-full" size="sm" onClick={() => onDelete(analysisId)}>
              {t('btn_delete_analysis')}
            </Button>
          </div>
        )}

        {analysis.status === 'ERROR' && analysis.error_message && (
          <p className="text-xs text-red-600 mt-2 break-words">{analysis.error_message}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalysisCard;